import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { aggregate, scoreColor, CATEGORY_ORDER } from './companyScore';

const DEFAULT_INCLUDE_WEIGHT = 0.06; // weight applied when (re)including a feature

// ---------------------------------------------------------------------------
// Method editor — include toggles + weight sliders, grouped by category
// ---------------------------------------------------------------------------

function MethodFeatureEditor({ catalog, weights, onChange, disabled }) {
  const groups = useMemo(() => {
    const m = new Map();
    for (const s of catalog || []) {
      if (!m.has(s.category)) m.set(s.category, []);
      m.get(s.category).push(s);
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) if (m.has(cat)) ordered.push([cat, m.get(cat)]);
    for (const [cat, sigs] of m) if (!CATEGORY_ORDER.includes(cat)) ordered.push([cat, sigs]);
    return ordered;
  }, [catalog]);

  const total = Object.values(weights || {}).reduce((a, b) => a + (Number(b) || 0), 0) || 1;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {groups.map(([cat, sigs]) => (
        <div key={cat}>
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">{cat}</div>
          {sigs.map((s) => {
            const w = Number(weights?.[s.key]) || 0;
            const included = w > 0;
            return (
              <div key={s.key} className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  checked={included}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(s.key, e.target.checked ? DEFAULT_INCLUDE_WEIGHT : 0)
                  }
                  className="accent-blue-500"
                  title={included ? 'Included' : 'Excluded'}
                />
                <span
                  className={`text-[11px] w-40 truncate ${included ? 'text-gray-400' : 'text-gray-600 line-through'}`}
                  title={s.label}
                >
                  {s.label}
                </span>
                <input
                  type="range"
                  min="0"
                  max="0.3"
                  step="0.005"
                  value={w}
                  disabled={disabled || !included}
                  onChange={(e) => onChange(s.key, parseFloat(e.target.value))}
                  className="flex-1 accent-blue-500 disabled:opacity-40"
                />
                <span className="text-[11px] text-gray-300 w-10 text-right">
                  {included ? `${Math.round((w / total) * 100)}%` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Live preview — every scored stock re-aggregated under the EDITING weights,
// so scores visibly change as the user drags sliders / toggles features.
// ---------------------------------------------------------------------------

function PreviewTable({ scored, weights }) {
  const rows = useMemo(
    () =>
      (scored || [])
        .map((r) => ({ ticker: r.ticker, result: aggregate(r.signals, weights) }))
        .sort((a, b) => (b.result.value ?? -1) - (a.result.value ?? -1)),
    [scored, weights],
  );

  if (!rows.length) {
    return (
      <p className="text-xs text-gray-500 italic">No scored stocks to preview yet.</p>
    );
  }

  return (
    <div className="overflow-x-auto border border-gray-800 rounded-lg">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800 bg-gray-900/60">
            <th className="py-2 px-3 text-left font-medium">#</th>
            <th className="py-2 px-3 text-left font-medium">Ticker</th>
            <th className="py-2 px-3 text-right font-medium">Score</th>
            {CATEGORY_ORDER.map((c) => (
              <th key={c} className="py-2 px-3 text-right font-medium whitespace-nowrap">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const catMap = Object.fromEntries(
              (row.result.categories || []).map((c) => [c.name, c.subscore]),
            );
            return (
              <tr key={row.ticker} className="border-b border-gray-800/60">
                <td className="py-2 px-3 text-gray-500">{i + 1}</td>
                <td className="py-2 px-3 font-semibold text-gray-100">{row.ticker}</td>
                <td className="py-2 px-3 text-right font-bold" style={{ color: scoreColor(row.result.value) }}>
                  {row.result.value == null ? '—' : Math.round(row.result.value)}
                </td>
                {CATEGORY_ORDER.map((c) => {
                  const v = catMap[c];
                  return (
                    <td key={c} className="py-2 px-3 text-right" style={{ color: scoreColor(v) }}>
                      {v == null ? '—' : Math.round(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

function CompanyScoreMethodEditorModal({
  isOpen,
  onClose,
  methods,
  templates,
  editorMethodId,
  editName,
  editWeights,
  scored,
  catalog,
  editorIsDefault,
  editorIsExisting,
  statusMsg,
  onSelectMethod,
  onLoadTemplate,
  onChangeName,
  onChangeWeight,
  onSave,
  onDelete,
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between px-5 py-4 border-b border-gray-700 bg-gray-900">
          <div>
            <h3 className="text-lg font-semibold text-white">Scoring methods</h3>
            <p className="text-xs text-gray-500">
              Pick the features and weights for a method. The table below previews every stock under
              the method you're editing — scores update live as you adjust.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Method selector / name / save / delete */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-400">Editing:</span>
            <select
              value={editorMethodId}
              onChange={(e) => onSelectMethod(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-2 py-1"
            >
              <option value="new">+ New method</option>
              <option value="default">Default (built-in)</option>
              {methods
                .filter((m) => !m.builtin)
                .map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
            </select>
            {templates && templates.length > 0 && (
              <select
                value=""
                onChange={(e) => {
                  const t = templates[Number(e.target.value)];
                  if (t) onLoadTemplate(t);
                }}
                className="bg-gray-800 border border-blue-600/60 rounded-md text-xs text-blue-200 px-2 py-1"
                title="Pre-fill a new method from a curated template"
              >
                <option value="">Start from template ▾</option>
                {templates.map((t, i) => (
                  <option key={t.name} value={i}>{t.name}</option>
                ))}
              </select>
            )}
            <input
              type="text"
              value={editName}
              onChange={(e) => onChangeName(e.target.value)}
              placeholder="Method name"
              disabled={editorIsDefault}
              className="bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-2 py-1 disabled:opacity-50"
            />
            <button
              onClick={onSave}
              disabled={editorIsDefault}
              className="px-3 py-1 text-xs rounded-md border border-green-600/60 text-green-300 hover:bg-green-600/20 disabled:opacity-40"
            >
              {editorIsExisting ? 'Update' : 'Save as new'}
            </button>
            {editorIsExisting && (
              <button
                onClick={onDelete}
                className="px-3 py-1 text-xs rounded-md border border-red-600/60 text-red-300 hover:bg-red-600/20"
              >
                Delete
              </button>
            )}
            {editorIsDefault && (
              <span className="text-[11px] text-gray-500">
                Built-in — pick “+ New method” to create an editable copy.
              </span>
            )}
            {statusMsg && <span className="text-xs text-gray-400">{statusMsg}</span>}
          </div>

          {/* Live preview of all scored stocks under the editing weights */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
              Live preview — all stocks under this method
            </div>
            <PreviewTable scored={scored} weights={editWeights} />
          </div>

          {/* Feature include toggles + weight sliders */}
          <div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
              Features &amp; weights
            </div>
            <MethodFeatureEditor
              catalog={catalog}
              weights={editWeights}
              onChange={onChangeWeight}
              disabled={editorIsDefault}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyScoreMethodEditorModal;
